import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  ArrowLeft, Mail, Phone, MapPin, Calendar, Banknote, Building2, CreditCard, Download, FileText, 
  UserX, AlertTriangle, User, Users, Home, FileCheck, History, Clock, Edit2, Trash2, Plus,
  Briefcase, Heart, Shield, GraduationCap, Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatutorySummaryCard } from '../components/employee/StatutorySummaryCard';
import { FinalSettlementPanel, FinalSettlementBreakdown } from '../components/employee/FinalSettlementPanel';
import { notifyPayslipSync } from '../lib/payslipSync';
import { TerminationActions } from '../components/employee/TerminationActions';
import { lengthOfService } from '../lib/serviceLength';
import { CountrySelect } from '../components/employee/CountrySelect';

// Iter88 (Ankur 09-Jul-2026) — Safe date formatter. `new Date(x)` on an
// empty string, null or garbage returns an object whose
// `.toLocaleDateString()` renders as the literal string "Invalid Date",
// which was surfacing on the Employee Details page for rows imported
// with a bad Joined Date. Fall back to a dash so HR can spot the row
// and fix the source data instead of showing a scary "Invalid Date".
const safeFormatDate = (raw, opts = { day: '2-digit', month: 'long', year: 'numeric' }) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-MY', opts);
};

export default function EmployeeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTerminateDialog, setShowTerminateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [auditLog, setAuditLog] = useState([]);
  const [leaveEntitlements, setLeaveEntitlements] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [documents, setDocuments] = useState([]);
  
  // Dialog states for editing
  const [showAddFamilyDialog, setShowAddFamilyDialog] = useState(false);
  const [showEditFamilyDialog, setShowEditFamilyDialog] = useState(false); // Iter75
  const [editingFamilyId, setEditingFamilyId] = useState(null);
  const [showAddDocumentDialog, setShowAddDocumentDialog] = useState(false);
  const [showEditLeaveDialog, setShowEditLeaveDialog] = useState(false);
  const [showEditPersonalDialog, setShowEditPersonalDialog] = useState(false);
  // Iter44 (Shikha 19-Jun-2026) — PIN/postcode field validation + auto state/country
  // lookup. Maintains its own state separate from form value so we can show
  // inline "Looking up..." / error feedback as the user types.
  const [postcodeLookup, setPostcodeLookup] = useState({ status: 'idle', message: '' });
  
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isManager = user.role === 'department_head';
  const canManage = ['admin', 'hr_manager'].includes(user.role);
  
  const [terminationData, setTerminationData] = useState({
    termination_date: new Date().toISOString().split('T')[0],
    reason_for_leaving: '',
    termination_type: 'resignation',
    notice_period_served: true,
    last_working_day: new Date().toISOString().split('T')[0],
    hr_feedback: '',
    manager_feedback: '',
    exit_interview_completed: false,
    assets_returned: false,
    final_settlement_amount: 0,
    remarks: ''
  });

  // Personal info form
  const [personalInfo, setPersonalInfo] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Malaysia',
    emergency_contact_name: '',
    emergency_contact_relation: '',
    emergency_contact_phone: '',
    emergency_contact_address: ''
  });

  // Family member form
  const [familyForm, setFamilyForm] = useState({
    name: '',
    relationship: 'spouse',
    ic_number: '',
    date_of_birth: '',
    occupation: '',
    is_dependent: true
  });

  // Leave entitlement form
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'annual',
    entitled_days: 14,
    carried_forward: 0,
    used_days: 0
  });

  useEffect(() => {
    fetchEmployee();
    fetchAuditLog();
    fetchLeaveEntitlements();
    fetchFamilyMembers();
    fetchDocuments();
  }, [id]);

  const fetchEmployee = async () => {
    try {
      const empResponse = await axios.get(`${API}/employees/${id}`);
      setEmployee(empResponse.data);
      
      // Set personal info from employee data
      if (empResponse.data.personal_info) {
        // Iter44 — merge over defaults to keep undefined fields as '' (avoids
        // React's "uncontrolled to controlled" warning). Also auto-sanitize
        // any legacy garbage postcode values: only keep digits, cap at 6.
        const pi = empResponse.data.personal_info || {};
        const cleanedPostcode = (pi.postcode || '').replace(/\D+/g, '').slice(0, 6);
        setPersonalInfo((prev) => ({
          ...prev,
          ...pi,
          postcode: cleanedPostcode,
        }));
      }
      
      try {
        const payslipResponse = await axios.get(`${API}/payslips`);
        const empPayslips = payslipResponse.data.filter(p => p.employee_id === id);
        setPayslips(empPayslips.sort((a, b) => b.year - a.year || b.month - a.month));
      } catch (payslipError) {
        console.warn('Failed to fetch payslips:', payslipError);
        setPayslips([]);
      }
    } catch (error) {
      toast.error('Failed to fetch employee details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const response = await axios.get(`${API}/employees/${id}/audit-log`);
      setAuditLog(response.data || []);
    } catch (error) {
      console.warn('Audit log not available');
      // Generate mock audit log from employee data
      setAuditLog([]);
    }
  };

  const fetchLeaveEntitlements = async () => {
    // Iter50 (Ankur 23-Jun-2026) — When a new employee has no entitlements
    // yet (e.g., just-onboarded "Pending Joining" → "Active"), the API
    // returns []. The Edit modal would then render zero rows, leaving HR
    // staring at an empty dialog. We seed from the company's configured
    // leave_types (or a sensible Malaysian default) so HR always has rows
    // to edit and the save round-trip creates the records.
    const seedFromLeaveTypes = async () => {
      try {
        const types = await axios.get(`${API}/leave/types`);
        if (Array.isArray(types.data) && types.data.length > 0) {
          return types.data.map((t) => ({
            leave_type: (t.leave_type_id || t.name || 'leave').toString().toLowerCase().replace(/\s+/g, '_'),
            leave_type_name: t.name,
            entitled_days: t.days_per_year ?? 0,
            carried_forward: 0,
            used_days: 0,
          }));
        }
      } catch {
        // fall through to hardcoded default
      }
      return [
        { leave_type: 'annual', entitled_days: 14, carried_forward: 0, used_days: 0 },
        { leave_type: 'medical', entitled_days: 14, carried_forward: 0, used_days: 0 },
        { leave_type: 'emergency', entitled_days: 3, carried_forward: 0, used_days: 0 },
      ];
    };
    try {
      const response = await axios.get(`${API}/employees/${id}/leave-entitlements`);
      const arr = Array.isArray(response.data) ? response.data : [];
      if (arr.length === 0) {
        setLeaveEntitlements(await seedFromLeaveTypes());
      } else {
        setLeaveEntitlements(arr);
      }
    } catch (error) {
      setLeaveEntitlements(await seedFromLeaveTypes());
    }
  };

  const fetchFamilyMembers = async () => {
    try {
      const response = await axios.get(`${API}/employees/${id}/family`);
      setFamilyMembers(response.data || []);
    } catch (error) {
      setFamilyMembers([]);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/employees/${id}/documents`);
      setDocuments(response.data || []);
    } catch (error) {
      setDocuments([]);
    }
  };

  const downloadForm = async (formType, year) => {
    try {
      toast.info('Generating form...');
      const response = await axios.get(
        `${API}/forms/download/${formType}?employee_id=${id}&year=${year}`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formType} Form_${employee.full_name}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Form downloaded successfully!');
    } catch (error) {
      console.error('Error downloading form:', error);
      // Iter88 — Surface the backend's actual detail (e.g.
      // "No payroll data found for 2026") so the tester knows WHY the
      // form failed. axios with responseType=blob returns the error body
      // as a Blob, so we read it back to JSON before falling back.
      let detail = '';
      try {
        const blob = error?.response?.data;
        if (blob && typeof blob.text === 'function') {
          const text = await blob.text();
          try { detail = JSON.parse(text)?.detail || ''; } catch { detail = text; }
        } else if (typeof error?.response?.data?.detail === 'string') {
          detail = error.response.data.detail;
        }
      } catch (_) {
        detail = '';
      }
      if (detail && /no payroll/i.test(detail)) {
        toast.error(`${detail}. Generate a payslip for this employee before downloading statutory forms.`);
      } else {
        toast.error(detail || 'Failed to download form');
      }
    }
  };

  // Live final-settlement estimate shown in the Terminate dialog
  const [fsEstimate, setFsEstimate] = useState(null);
  useEffect(() => {
    if (!showTerminateDialog || !terminationData.last_working_day) { setFsEstimate(null); return undefined; }
    const t = setTimeout(() => {
      axios.get(`${API}/employees/${id}/final-settlement`, { params: { last_working_day: terminationData.last_working_day } })
        .then((r) => setFsEstimate(r.data))
        .catch(() => setFsEstimate(null));
    }, 300);
    return () => clearTimeout(t);
  }, [showTerminateDialog, terminationData.last_working_day, id]);

  const handleTerminate = async () => {
    if (!terminationData.reason_for_leaving.trim()) {
      toast.error('Please provide a reason for leaving');
      return;
    }

    try {
      const res = await axios.post(`${API}/employees/${id}/terminate`, terminationData);
      toast.success('Employee terminated successfully');
      notifyPayslipSync(res.data?.payslip_sync);
      setShowTerminateDialog(false);
      fetchEmployee();
    } catch (error) {
      console.error('Error terminating employee:', error);
      // Iter88h — Surface the backend's actual error detail (e.g. the
      // protected-demo-account guard) instead of the generic "Failed".
      // Reported by Ankur 15-Jun-2026: terminating Lee Wei Ming (manager
      // demo account) showed an opaque toast that didn't explain why.
      const detail =
        error?.response?.data?.detail ||
        (typeof error?.response?.data === 'string' ? error.response.data : '') ||
        '';
      toast.error(detail || 'Failed to terminate employee');
    }
  };

  const handleSavePersonalInfo = async () => {
    // Iter44 — Block save if PIN code looks invalid (must be 5 or 6 digits).
    const pc = (personalInfo.postcode || '').trim();
    if (pc && !/^\d{5,6}$/.test(pc)) {
      toast.error('PIN / Postcode must be 5 digits (Malaysia) or 6 digits (India). Letters and spaces are not allowed.');
      return;
    }
    try {
      await axios.put(`${API}/employees/${id}/personal-info`, personalInfo);
      toast.success('Personal information updated');
      setShowEditPersonalDialog(false);
      fetchEmployee();
    } catch (error) {
      toast.error('Failed to update personal information');
    }
  };

  // Iter44 — Live PIN / Postcode lookup. Uses Indian Post API for 6 digits and
  // Zippopotam (free, CORS-enabled) for 5-digit Malaysian postcodes. Populates
  // `state` and `country` from the response, and surfaces inline status to the
  // user so they know whether the lookup succeeded.
  const handlePostcodeChange = async (raw) => {
    // Strip everything that isn't a digit; clamp to 6 chars (longest supported).
    const digits = (raw || '').replace(/\D+/g, '').slice(0, 6);
    setPersonalInfo((prev) => ({ ...prev, postcode: digits }));

    if (digits.length < 5) {
      setPostcodeLookup({ status: 'idle', message: '' });
      return;
    }
    setPostcodeLookup({ status: 'loading', message: 'Looking up location…' });

    try {
      if (digits.length === 6) {
        // Indian PIN code
        const res = await fetch(`https://api.postalpincode.in/pincode/${digits}`);
        const data = await res.json();
        const office = data?.[0]?.PostOffice?.[0];
        if (data?.[0]?.Status === 'Success' && office) {
          setPersonalInfo((prev) => ({
            ...prev,
            state: office.State || prev.state,
            country: office.Country || 'India',
            city: prev.city || office.District || '',
          }));
          setPostcodeLookup({ status: 'success', message: `${office.District}, ${office.State}, ${office.Country}` });
          return;
        }
        setPostcodeLookup({ status: 'error', message: 'No location found for this 6-digit PIN.' });
        return;
      }

      // 5 digits — try Malaysia first then Singapore as a sensible fallback.
      for (const cc of ['MY', 'SG']) {
        try {
          const res = await fetch(`https://api.zippopotam.us/${cc}/${digits}`);
          if (res.ok) {
            const data = await res.json();
            const place = data?.places?.[0];
            if (place) {
              setPersonalInfo((prev) => ({
                ...prev,
                state: place.state || prev.state,
                country: data.country || (cc === 'MY' ? 'Malaysia' : 'Singapore'),
                city: prev.city || place['place name'] || '',
              }));
              setPostcodeLookup({
                status: 'success',
                message: `${place['place name']}, ${place.state}, ${data.country}`,
              });
              return;
            }
          }
        } catch {
          // try next country
        }
      }
      setPostcodeLookup({ status: 'error', message: 'No location found. Please fill State/Country manually.' });
    } catch {
      setPostcodeLookup({ status: 'error', message: 'Lookup failed (network). Please fill State/Country manually.' });
    }
  };

  const handleAddFamily = async () => {
    try {
      await axios.post(`${API}/employees/${id}/family`, familyForm);
      toast.success('Family member added');
      setShowAddFamilyDialog(false);
      setFamilyForm({ name: '', relationship: 'spouse', ic_number: '', date_of_birth: '', occupation: '', is_dependent: true });
      fetchFamilyMembers();
    } catch (error) {
      toast.error('Failed to add family member');
    }
  };

  const handleDeleteFamily = async (familyId) => {
    if (!confirm('Are you sure you want to remove this family member?')) return;
    try {
      await axios.delete(`${API}/employees/${id}/family/${familyId}`);
      toast.success('Family member removed');
      fetchFamilyMembers();
    } catch (error) {
      toast.error('Failed to remove family member');
    }
  };

  // Iter75 — Edit Family Member
  const openEditFamily = (member) => {
    setEditingFamilyId(member.id);
    setFamilyForm({
      name: member.name || '',
      relationship: member.relationship || 'spouse',
      ic_number: member.ic_number || '',
      date_of_birth: member.date_of_birth || '',
      occupation: member.occupation || '',
      is_dependent: member.is_dependent !== false,
    });
    setShowEditFamilyDialog(true);
  };

  const handleUpdateFamily = async () => {
    if (!editingFamilyId) return;
    try {
      await axios.put(`${API}/employees/${id}/family/${editingFamilyId}`, familyForm);
      toast.success('Family member updated');
      setShowEditFamilyDialog(false);
      setEditingFamilyId(null);
      fetchFamilyMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update family member');
    }
  };

  const handleUpdateLeaveEntitlement = async () => {
    try {
      // Iter121 — Send the full edited array so all leave types are saved.
      // Reported by Ankur 17-Jun-2026: previous code sent stale `leaveForm`
      // single-record state, so dialog edits were silently dropped.
      await axios.put(`${API}/employees/${id}/leave-entitlements`, leaveEntitlements);
      toast.success('Leave entitlements updated');
      setShowEditLeaveDialog(false);
      fetchLeaveEntitlements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update leave entitlements');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Employee not found</p>
      </div>
    );
  }

  // Length of service runs to the last working day for leavers, otherwise to today.
  const serviceEndDate = employee.status === 'terminated' ? employee.termination_details?.last_working_day : null;
  const serviceLength = lengthOfService(employee.join_date, serviceEndDate);


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button variant="ghost" onClick={() => navigate('/dashboard/employees')} data-testid="back-to-employees">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 truncate">Employee Details</h1>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Button variant="outline" onClick={() => navigate(`/dashboard/employees/${id}/edit`)} className="border-slate-300 dark:border-slate-600">
              Edit Employee
            </Button>
            {employee.status === 'active' && (() => {
              // Iter88h — Demo-account guard on the UI side. The backend
              // also blocks (and returns a 400), but disabling the button
              // here gives a clearer signal so the tester doesn't even
              // click a control that's guaranteed to fail.
              const PROTECTED_EMAILS = new Set([
                'hr@myhrorbit.com', 'employee1@myhrorbit.com',
                'employee2@myhrorbit.com', 'manager@myhrorbit.com',
                'superadmin@myhrorbit.com',
              ]);
              const isDemo = PROTECTED_EMAILS.has((employee.email || '').toLowerCase());
              return (
                <Button
                  variant="destructive"
                  onClick={() => setShowTerminateDialog(true)}
                  disabled={isDemo}
                  data-testid="terminate-employee-btn"
                  title={isDemo
                    ? 'This is a protected demo account used by the testing team and cannot be terminated.'
                    : 'Terminate this employee'}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <UserX className="h-4 w-4 mr-2" />
                  Terminate Employee
                </Button>
              );
            })()}
          </div>
        )}
      </div>

      {/* Profile Header */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
        <div className="flex items-start space-x-6">
          <EmployeePhotoBlock
            employee={employee}
            canManage={canManage}
            onChanged={async () => {
              try {
                const res = await axios.get(`${API}/employees/${id}`);
                setEmployee(res.data);
              } catch (_) { /* ignore */ }
            }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">{employee.full_name}</h2>
              {/* Iter131 — Three status pills: active (green), on notice
                  (amber) and terminated (red). Reported by Ankur
                  19-Jun-2026: future-dated resignations made employees
                  appear "Terminated" while they were still working out
                  their notice. */}
              {(() => {
                const s = employee.status;
                if (s === 'on_notice_period') {
                  return (
                    <span
                      data-testid="employee-status-badge"
                      className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                      title={`Last working day: ${(employee.termination_details && employee.termination_details.last_working_day) || employee.resignation_date || '—'}`}
                    >
                      On Notice Period
                    </span>
                  );
                }
                return (
                  <span
                    data-testid="employee-status-badge"
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      s === 'active'
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    }`}
                  >
                    {s}
                  </span>
                );
              })()}
            </div>
            <p className="text-slate-600 dark:text-slate-400 text-lg mb-4">{employee.position || 'N/A'} • {employee.department || 'N/A'}</p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400 min-w-0">
                <Mail className="h-4 w-4 shrink-0" />
                <span className="break-all">{employee.email}</span>
              </div>
              {employee.phone && (
                <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                  <Phone className="h-4 w-4" />
                  <span>{employee.phone}</span>
                </div>
              )}
              <div className="flex items-center space-x-2 text-slate-600 dark:text-slate-400">
                <Calendar className="h-4 w-4" />
                <span>Joined {safeFormatDate(employee.join_date, { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                {serviceLength && (
                  <span className="inline-flex items-center rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-xs font-medium" data-testid="service-length-badge" title={`Length of service: ${serviceLength.label}`}>
                    {serviceLength.label}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-7 lg:w-auto lg:inline-flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="compensation" className="flex items-center gap-2">
            <Banknote className="h-4 w-4" />
            <span className="hidden sm:inline">Compensation</span>
          </TabsTrigger>
          <TabsTrigger value="personal" className="flex items-center gap-2">
            <Home className="h-4 w-4" />
            <span className="hidden sm:inline">Personal</span>
          </TabsTrigger>
          <TabsTrigger value="family" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Family</span>
          </TabsTrigger>
          <TabsTrigger value="leave" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">Leave</span>
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileCheck className="h-4 w-4" />
            <span className="hidden sm:inline">Documents</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">History</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Employment Information */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-emerald-500" />
                Employment Information
              </h3>
              <div className="space-y-3">
                <InfoRow label="Employee ID" value={employee.employee_id} />
                <InfoRow label="Join Date" value={safeFormatDate(employee.join_date)} />
                <InfoRow
                  label="Length of Service"
                  value={<span data-testid="service-length-value">{serviceLength ? serviceLength.label : 'N/A'}</span>}
                  sub={serviceLength ? (serviceEndDate ? `Up to last working day ${safeFormatDate(serviceEndDate)}` : `As of today · ${serviceLength.totalDays.toLocaleString()} days in total`) : null}
                />
                <InfoRow label="Employment Type" value={employee.employment_type?.replace('_', ' ').toUpperCase() || 'Full Time'} />
                <InfoRow label="Designated Rest Day" value={<span data-testid="employee-rest-day-value">{employee.rest_day ? employee.rest_day.charAt(0).toUpperCase() + employee.rest_day.slice(1) : 'Company default'}</span>} sub="Rest-day overtime rates apply only on this day" />
                <InfoRow label="NRIC No." value={employee.ic_number || 'N/A'} />
                <InfoRow label="Nationality" value={employee.nationality?.replace(/_/g, ' ').toUpperCase() || 'Malaysian'} />
                {employee.nationality && employee.nationality !== 'malaysian' && (
                  <>
                    <InfoRow
                      label="Passport No."
                      value={employee.passport_number || 'Not provided'}
                      sub={employee.passport_expiry ? `Expires ${safeFormatDate(employee.passport_expiry)}` : null}
                    />
                    <InfoRow
                      label="Work Visa / Permit"
                      value={<span data-testid="emp-visa-reference-value">{employee.visa_reference || 'Not provided'}</span>}
                      sub={employee.visa_expiry ? `Expires ${safeFormatDate(employee.visa_expiry)}` : null}
                    />
                  </>
                )}
                <InfoRow label="Religion" value={employee.religion || 'N/A'} />
                <InfoRow
                  label="Highest Qualification"
                  value={employee.highest_qualification?.level || 'N/A'}
                  sub={[employee.highest_qualification?.field_of_study, employee.highest_qualification?.institution, employee.highest_qualification?.year_completed].filter(Boolean).join(' · ') || null}
                />
                <InfoRow label="Marital Status" value={employee.marital_status?.replace('_', ' ').toUpperCase() || 'N/A'} />
                {employee.reporting_manager && (
                  <InfoRow label="Reports To" value={employee.reporting_manager} />
                )}
              </div>
            </div>

            {/* Statutory Information */}
            {!isManager && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-500" />
                  Malaysia Statutory Info
                </h3>
                <div className="space-y-3">
                  <InfoRow label="EPF Number" value={employee.epf_number || 'N/A'} />
                  <InfoRow label="SOCSO Number" value={employee.socso_number || 'N/A'} />
                  <InfoRow
                    label="EIS Number"
                    value={employee.eis_number || 'N/A'}
                    note={
                      (employee.eis_number || '').startsWith('PENDING-PERKESO-')
                        ? 'Placeholder — register with PERKESO/SOCSO and update via Compensation tab.'
                        : (!employee.eis_number && employee.socso_number)
                          ? 'Will default to SOCSO Number on next payroll run.'
                          : null
                    }
                  />
                  <InfoRow
                    label="Tax Number"
                    value={employee.tax_number || 'N/A'}
                    note={
                      (employee.tax_number || '').startsWith('PENDING-LHDN-')
                        ? 'Placeholder — register with LHDN and update via Compensation tab.'
                        : null
                    }
                  />
                </div>
              </div>
            )}

            {/* Statutory applicability + TP1 reliefs */}
            {!isManager && <StatutorySummaryCard employee={employee} />}

            {/* Bank Information */}
            {!isManager && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-purple-500" />
                  Bank Information
                </h3>
                <div className="space-y-3">
                  <InfoRow label="Bank Name" value={employee.bank_name || 'N/A'} />
                  <InfoRow label="Bank Account" value={employee.bank_account || 'N/A'} />
                </div>
              </div>
            )}

            {/* Statutory Forms */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Download Statutory Forms ({new Date().getFullYear()})</h3>
              {/* Iter88 — When no payslip exists for the current year, the
                  backend correctly returns 404. Surface the empty state
                  inline (with a link to Payroll) so testers don't have to
                  click to discover the cause. */}
              {(() => {
                const currentYear = new Date().getFullYear();
                const yearPayslips = (payslips || []).filter(p => Number(p?.year) === currentYear);
                const hasPayslip = yearPayslips.length > 0;
                return (
                  <>
                    {!hasPayslip && (
                      <div
                        data-testid="statutory-forms-empty-state"
                        className="mb-4 rounded-lg border border-amber-200 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200"
                      >
                        No payroll data found for {currentYear}. Generate at least one payslip for this employee in <a href="/dashboard/payroll" className="font-semibold underline hover:text-amber-900 dark:hover:text-amber-100">Payroll</a> before downloading EA / Form E / PCB2(II) / CP21.
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      {['EA', 'FORM_E', 'PCB2_II', 'CP21'].map(form => (
                        <Button
                          key={form}
                          variant="outline"
                          disabled={!hasPayslip}
                          data-testid={`download-form-${form.toLowerCase()}-btn`}
                          onClick={() => downloadForm(form, currentYear)}
                          className="flex flex-col items-center justify-center h-20 border-slate-300 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-100 hover:bg-emerald-50 hover:border-emerald-300 dark:hover:bg-emerald-900/30 dark:hover:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={hasPayslip ? `Download ${form.replace('_', ' ')}` : `Generate payroll for ${currentYear} to enable this form`}
                        >
                          <Download className="h-4 w-4 mb-1" />
                          <span className="text-sm font-medium">{form.replace('_', ' ')}</span>
                        </Button>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Iter135 — Show the resignation / termination details panel
              for BOTH `terminated` and `on_notice_period`. Previously
              the guard was `status === 'terminated'` only, so notice-
              period employees (introduced in Iter131) had no visibility
              of their Last Working Day / Final Settlement / Reason from
              the profile page. Reported by Ankur 19-Jun-2026.
              The panel is amber for on-notice (still active) and red
              for fully terminated. */}
          {(employee.status === 'terminated' || employee.status === 'on_notice_period') && employee.termination_details && (
            (() => {
              const isOnNotice = employee.status === 'on_notice_period';
              const wrapCls = isOnNotice
                ? 'mt-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6'
                : 'mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6';
              const iconCls = isOnNotice ? 'h-5 w-5 text-amber-600' : 'h-5 w-5 text-red-600';
              const titleCls = isOnNotice
                ? 'text-lg font-semibold text-amber-900 dark:text-amber-100'
                : 'text-lg font-semibold text-red-900 dark:text-red-100';
              const title = isOnNotice ? 'Resignation Details (On Notice Period)' : 'Termination Details';
              const td = employee.termination_details;
              return (
                <div className={wrapCls} data-testid="termination-details-panel">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className={iconCls} />
                      <h3 className={titleCls}>{title}</h3>
                    </div>
                    {canManage && <TerminationActions employee={employee} onUpdated={fetchEmployee} />}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <InfoRow
                      label="Resignation Submitted"
                      value={td.termination_date ? new Date(td.termination_date).toLocaleDateString('en-MY') : 'N/A'}
                    />
                    <InfoRow
                      label="Last Working Day"
                      value={td.last_working_day ? new Date(td.last_working_day).toLocaleDateString('en-MY') : 'N/A'}
                    />
                    <InfoRow label="Type" value={(td.termination_type || 'resignation').replace('_', ' ').toUpperCase()} />
                    <InfoRow label="Final Settlement" value={`RM ${(td.final_settlement_amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`} bold />
                    <FinalSettlementPanel employee={employee} canManage={canManage} onUpdated={fetchEmployee} />
                    {isOnNotice && (() => {
                      try {
                        const lwd = new Date(td.last_working_day);
                        const today = new Date();
                        const daysLeft = Math.ceil((lwd - today) / 86400000);
                        return (
                          <InfoRow
                            label="Days Until Last Working Day"
                            value={daysLeft > 0 ? `${daysLeft} day${daysLeft === 1 ? '' : 's'}` : `Today (${daysLeft <= 0 ? 'flips to terminated on next refresh' : ''})`}
                          />
                        );
                      } catch (_) { return null; }
                    })()}
                    <div className="col-span-2">
                      <InfoRow label="Reason for Resignation" value={td.reason_for_leaving || 'N/A'} />
                    </div>
                  </div>
                </div>
              );
            })()
          )}
        </TabsContent>

        {/* Compensation Tab */}
        <TabsContent value="compensation" className="mt-6">
          {/* Iter75 — Reconcile "Fixed Allowances" with the 5 individual
              allowance categories. Reported by Ankur 02-Jun-2026: Salary
              Breakdown showed RM 100 but every individual row showed RM 0
              because the legacy `salary_allowances` lump field had drifted
              from the new per-category fields. We now ALWAYS compute the
              fixed-allowance total as the sum of the per-category fields,
              and surface a clear warning chip when the legacy lump
              doesn't match. */}
          {(() => { /* presence only — variables computed inline below */ return null; })()}
          {(() => null)()}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Salary Breakdown */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Banknote className="h-5 w-5 text-emerald-500" />
                Salary Breakdown
              </h3>
              {(() => {
                // Iter129 — Read the same `*_allowance` fields that the
                // Edit Employee form writes and that the payroll
                // background task consumes. Previously this read a
                // different naming style (`allowance_*`) that was never
                // populated, so the Compensation tab always showed RM 0
                // for every allowance row. Reported by Ankur 19-Jun-2026.
                const allowSum = (employee.transport_allowance || 0)
                  + (employee.meal_allowance || 0)
                  + (employee.housing_allowance || 0)
                  + (employee.other_allowance || 0);
                const lump = employee.salary_allowances || 0;
                const fixedAllowances = allowSum > 0 ? allowSum : lump;
                const showMismatch = lump > 0 && allowSum > 0 && Math.abs(lump - allowSum) > 0.01;
                return (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                      <span className="text-slate-600 dark:text-slate-400">Basic Salary</span>
                      <span className="font-semibold text-slate-900 dark:text-slate-100">
                        RM {(employee.salary_basic || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                      <span className="text-slate-600 dark:text-slate-400">Fixed Allowances</span>
                      <span className="font-semibold text-slate-900 dark:text-slate-100" data-testid="comp-fixed-allowances">
                        RM {fixedAllowances.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    {showMismatch && (
                      <div className="text-xs p-2 rounded-md bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 text-amber-800 dark:text-amber-200" data-testid="allowance-mismatch-warning">
                        ⚠️ Legacy <code>salary_allowances</code> = RM {lump.toFixed(2)} doesn&apos;t match the sum of individual allowances (RM {allowSum.toFixed(2)}). Re-save this employee to reconcile.
                      </div>
                    )}
                    <div className="flex justify-between items-center py-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-3">
                      <span className="font-semibold text-emerald-700 dark:text-emerald-400">Gross Salary</span>
                      <span className="font-bold text-emerald-700 dark:text-emerald-400 text-lg" data-testid="comp-gross-salary">
                        RM {((employee.salary_basic || 0) + fixedAllowances).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Allowances Detail */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Allowances</h3>
              <div className="space-y-3">
                <InfoRow label="Transport Allowance" value={`RM ${(employee.transport_allowance || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`} />
                <InfoRow label="Meal Allowance" value={`RM ${(employee.meal_allowance || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`} />
                <InfoRow label="Housing Allowance" value={`RM ${(employee.housing_allowance || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`} />
                <InfoRow label="Other Allowance" value={`RM ${(employee.other_allowance || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`} />
              </div>
            </div>

            {/* Statutory Deductions */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Statutory Deductions (Monthly)</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">EPF (Employee 11%)</span>
                  <span className="text-red-600">- RM {((employee.salary_basic || 0) * 0.11).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">SOCSO (Employee)</span>
                  <span className="text-red-600">- RM {Math.min((employee.salary_basic || 0) * 0.005, 19.75).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">EIS (0.2%)</span>
                  <span className="text-red-600">- RM {(Math.min(employee.salary_basic || 0, 4000) * 0.002).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">PCB (Est.)</span>
                  <span className="text-red-600">- RM {(employee.pcb_amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {/* Employer Contributions */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Employer Contributions</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">EPF (Employer {(employee.salary_basic || 0) <= 5000 ? '13%' : '12%'})</span>
                  <span className="text-blue-600">RM {((employee.salary_basic || 0) * ((employee.salary_basic || 0) <= 5000 ? 0.13 : 0.12)).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">SOCSO (Employer)</span>
                  <span className="text-blue-600">RM {Math.min((employee.salary_basic || 0) * 0.0175, 69.05).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">EIS (Employer 0.2%)</span>
                  <span className="text-blue-600">RM {(Math.min(employee.salary_basic || 0, 4000) * 0.002).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">HRDF (1%)</span>
                  <span className="text-blue-600">RM {((employee.salary_basic || 0) * 0.01).toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {/* Recent Payslips */}
            <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Recent Payslips</h3>
              {payslips.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {payslips.slice(0, 6).map((payslip, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-slate-100">
                          {new Date(0, payslip.month - 1).toLocaleString('default', { month: 'long' })} {payslip.year}
                        </p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          Net: RM {payslip.net_salary?.toLocaleString('en-MY', { minimumFractionDigits: 2 }) || '0.00'}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm">
                        <FileText className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No payslips found</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Personal Tab */}
        <TabsContent value="personal" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Address */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Home className="h-5 w-5 text-blue-500" />
                  Address
                </h3>
                {canManage && (
                  <Button variant="ghost" size="sm" onClick={() => setShowEditPersonalDialog(true)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                <InfoRow label="Address Line 1" value={employee.personal_info?.address_line1 || 'Not provided'} />
                <InfoRow label="Address Line 2" value={employee.personal_info?.address_line2 || '-'} />
                <InfoRow label="City" value={employee.personal_info?.city || 'Not provided'} />
                <InfoRow label="State" value={employee.personal_info?.state || 'Not provided'} />
                <InfoRow label="Postcode" value={employee.personal_info?.postcode || 'Not provided'} />
                <InfoRow label="Country" value={employee.personal_info?.country || 'Malaysia'} />
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Phone className="h-5 w-5 text-red-500" />
                  Emergency Contact
                </h3>
                {canManage && (
                  <Button variant="ghost" size="sm" onClick={() => setShowEditPersonalDialog(true)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                <InfoRow label="Contact Name" value={employee.personal_info?.emergency_contact_name || 'Not provided'} />
                <InfoRow label="Relationship" value={employee.personal_info?.emergency_contact_relation || 'Not provided'} />
                <InfoRow label="Phone Number" value={employee.personal_info?.emergency_contact_phone || 'Not provided'} />
                <InfoRow label="Address" value={employee.personal_info?.emergency_contact_address || 'Not provided'} />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Family Tab */}
        <TabsContent value="family" className="mt-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-500" />
                Family Members
              </h3>
              {canManage && (
                <Button
                  onClick={() => setShowAddFamilyDialog(true)}
                  size="sm"
                  data-testid="add-family-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white border-0 shadow-sm dark:bg-indigo-500 dark:hover:bg-indigo-600 dark:text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Family Member
                </Button>
              )}
            </div>
            
            {familyMembers.length > 0 ? (
              <div className="space-y-4">
                {familyMembers.map((member, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Heart className="h-6 w-6 text-purple-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-slate-100">{member.name}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {member.relationship?.replace('_', ' ').toUpperCase()} • {member.is_dependent ? 'Dependent' : 'Non-Dependent'}
                        </p>
                        {member.ic_number && <p className="text-xs text-slate-500">IC: {member.ic_number}</p>}
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-2">
                        {/* Iter75 — explicit Edit + Delete buttons. Use
                            tinted backgrounds so they stay visible in
                            both light and dark mode (the previous ghost
                            variant rendered nearly invisible in dark
                            mode). */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditFamily(member)}
                          className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                          data-testid={`edit-family-${member.id}`}
                          title="Edit family member"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteFamily(member.id)}
                          className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                          data-testid={`delete-family-${member.id}`}
                          title="Delete family member"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No family members added</p>
                {canManage && (
                  <Button onClick={() => setShowAddFamilyDialog(true)} variant="outline" className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Family Member
                  </Button>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Leave Entitlement Tab */}
        <TabsContent value="leave" className="mt-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-orange-500" />
                Leave Entitlements ({new Date().getFullYear()})
              </h3>
              {canManage && (
                <Button onClick={() => setShowEditLeaveDialog(true)} variant="outline" size="sm">
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Entitlements
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {leaveEntitlements.map((leave, index) => (
                <div key={index} className="p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-slate-900 dark:text-slate-100 capitalize">
                      {/* Iter54 (Ankur Bug C) — Prefer the human-readable
                          leave_type_name from the backend so legacy short
                          codes like "ann" / "mc" don't render as "Ann Leave"
                          / "Mc Leave" in the UI. Falls back to a formatted
                          leave_type for older records without the name. */}
                      {leave.leave_type_name
                        || `${(leave.leave_type || '').replace(/_/g, ' ')} Leave`}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Entitled</span>
                      <span className="font-medium">{leave.entitled_days} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Carried Forward</span>
                      <span className="font-medium">{leave.carried_forward || 0} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Used</span>
                      <span className="font-medium text-red-500">{leave.used_days || 0} days</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
                      <span className="text-slate-700 dark:text-slate-300 font-medium">Balance</span>
                      <span className="font-bold text-emerald-600">
                        {(leave.entitled_days || 0) + (leave.carried_forward || 0) - (leave.used_days || 0)} days
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-teal-500" />
                Employee Documents
              </h3>
              {canManage && (
                <Button
                  onClick={() => setShowAddDocumentDialog(true)}
                  size="sm"
                  data-testid="upload-document-btn"
                  className="bg-teal-600 hover:bg-teal-700 text-white border-0 shadow-sm dark:bg-teal-500 dark:hover:bg-teal-600 dark:text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              )}
            </div>

            {documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documents.map((doc, index) => (
                  <div key={doc.id || index} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-blue-500" />
                      <div>
                        <p className="font-medium text-slate-900 dark:text-slate-100">{doc.name}</p>
                        <p className="text-xs text-slate-500">
                          {doc.category} • {doc.uploaded_date ? new Date(doc.uploaded_date).toLocaleDateString('en-MY') : ''}
                          {doc.size_bytes ? ` • ${(doc.size_bytes / 1024).toFixed(1)} KB` : ''}
                        </p>
                      </div>
                    </div>
                    {doc.download_url && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`download-doc-${doc.id || index}`}
                          onClick={async () => {
                            try {
                              const token =
                                localStorage.getItem('hrms_token') ||
                                sessionStorage.getItem('hrms_token') ||
                                '';
                              const resp = await fetch(`${API}${doc.download_url.replace(/^\/api/, '')}`, {
                                headers: token ? { Authorization: `Bearer ${token}` } : {},
                              });
                              if (!resp.ok) throw new Error('Download failed');
                              const blob = await resp.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = doc.original_filename || doc.name || 'document';
                              a.click();
                              window.URL.revokeObjectURL(url);
                            } catch (err) {
                              toast.error('Failed to download document');
                            }
                          }}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {/* Iter137 — Delete button (HR-only). Reported by
                            Shikha 19-Jun-2026: no way to remove a wrong
                            upload. The backend `_can_manage_employees`
                            check rejects employee-self deletion. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-doc-${doc.id || index}`}
                          onClick={async () => {
                            if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
                            try {
                              const fileId = (doc.id || '').slice(-8) || doc.stored_filename;
                              await axios.delete(`${API}/employees/${employee.employee_id}/documents/${fileId}`);
                              toast.success('Document deleted');
                              fetchDocuments();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to delete document');
                            }
                          }}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No documents uploaded</p>
                <div className="mt-4 text-sm text-slate-400">
                  <p>Recommended documents:</p>
                  <ul className="mt-2 space-y-1">
                    <li>• IC Copy</li>
                    <li>• Offer Letter</li>
                    <li>• Educational Certificates</li>
                    <li>• Professional Certifications</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* History/Audit Tab */}
        <TabsContent value="history" className="mt-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-500" />
              Change History & Audit Log
            </h3>
            
            {auditLog.length > 0 ? (
              <div className="space-y-4">
                {/* Iter73 — sort newest first */}
                {[...auditLog]
                  .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
                  .map((log, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl" data-testid={`audit-entry-${index}`}>
                    <div className="h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-slate-100">{log.action}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{log.details}</p>
                      {/* Iter73 — Field-level diff table when the backend
                          provides a structured changes array. */}
                      {Array.isArray(log.changes) && log.changes.length > 0 && (
                        <div className="mt-2 overflow-x-auto">
                          <table className="text-xs text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-md">
                            <thead className="bg-slate-100 dark:bg-slate-800">
                              <tr>
                                <th className="px-2 py-1 text-left font-semibold">Field</th>
                                <th className="px-2 py-1 text-left font-semibold">Before</th>
                                <th className="px-2 py-1 text-left font-semibold">After</th>
                              </tr>
                            </thead>
                            <tbody>
                              {log.changes.map((c, j) => (
                                <tr key={j} className="border-t border-slate-200 dark:border-slate-700">
                                  <td className="px-2 py-1 font-mono">{c.field}</td>
                                  <td className="px-2 py-1 text-slate-500 line-through">{c.from || '∅'}</td>
                                  <td className="px-2 py-1 text-emerald-600 dark:text-emerald-400">{c.to || '∅'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mt-2">
                        By {log.changed_by}{log.changed_by_role ? ` (${log.changed_by_role})` : ''} • {new Date(log.timestamp).toLocaleString('en-MY')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No change history available</p>
                <p className="text-sm text-slate-400 mt-2">Changes will be tracked automatically</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Terminate Dialog */}
      <Dialog open={showTerminateDialog} onOpenChange={setShowTerminateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-red-600">Terminate Employee</DialogTitle>
            <DialogDescription>
              This action will terminate {employee.full_name}'s employment. Please fill in the termination details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Termination Date</Label>
              <Input type="date" value={terminationData.termination_date}
                onChange={(e) => setTerminationData({...terminationData, termination_date: e.target.value})} />
            </div>
            <div>
              <Label>Last Working Day</Label>
              <Input type="date" value={terminationData.last_working_day}
                onChange={(e) => setTerminationData({...terminationData, last_working_day: e.target.value})} />
            </div>
            <div>
              <Label>Termination Type</Label>
              <select value={terminationData.termination_type}
                onChange={(e) => setTerminationData({...terminationData, termination_type: e.target.value})}
                className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-800">
                <option value="resignation">Resignation</option>
                <option value="termination">Termination</option>
                <option value="retirement">Retirement</option>
                <option value="contract_end">Contract End</option>
                <option value="mutual_agreement">Mutual Agreement</option>
              </select>
            </div>
            <div className="col-span-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/10 p-3" data-testid="terminate-fs-estimate">
              <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200 mb-1">Final Settlement (auto-calculated)</p>
              {fsEstimate ? <FinalSettlementBreakdown breakdown={fsEstimate} compact /> : <p className="text-xs text-slate-500">Calculating from last working day…</p>}
              <p className="text-[11px] text-slate-500 mt-2">Recomputed automatically once the final month&apos;s payroll is processed.</p>
            </div>
            <div className="col-span-2">
              <Label>Reason for Leaving *</Label>
              <textarea value={terminationData.reason_for_leaving}
                onChange={(e) => setTerminationData({...terminationData, reason_for_leaving: e.target.value})}
                className="w-full h-24 rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 bg-white dark:bg-slate-800"
                placeholder="Please provide a detailed reason..." />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowTerminateDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleTerminate}>Confirm Termination</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Family Dialog */}
      <Dialog open={showAddFamilyDialog} onOpenChange={setShowAddFamilyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Family Member</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="col-span-2">
              <Label>Full Name</Label>
              <Input value={familyForm.name} onChange={(e) => setFamilyForm({...familyForm, name: e.target.value})} />
            </div>
            <div>
              <Label>Relationship</Label>
              <select value={familyForm.relationship} onChange={(e) => setFamilyForm({...familyForm, relationship: e.target.value})}
                className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-800">
                <option value="spouse">Spouse</option>
                <option value="child">Child</option>
                <option value="parent">Parent</option>
                <option value="sibling">Sibling</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>IC Number</Label>
              <Input value={familyForm.ic_number} onChange={(e) => setFamilyForm({...familyForm, ic_number: e.target.value})} />
            </div>
            <div>
              <Label>Date of Birth</Label>
              <Input type="date" value={familyForm.date_of_birth} onChange={(e) => setFamilyForm({...familyForm, date_of_birth: e.target.value})} />
            </div>
            <div>
              <Label>Occupation</Label>
              <Input value={familyForm.occupation} onChange={(e) => setFamilyForm({...familyForm, occupation: e.target.value})} />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={familyForm.is_dependent} onChange={(e) => setFamilyForm({...familyForm, is_dependent: e.target.checked})} />
              <Label>Is Dependent (for tax purposes)</Label>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowAddFamilyDialog(false)}>Cancel</Button>
            <Button onClick={handleAddFamily}>Add Family Member</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter75 — Edit Family Member Dialog (reuses the same familyForm
          state; openEditFamily prefills it before showing this dialog). */}
      <Dialog open={showEditFamilyDialog} onOpenChange={(o) => { setShowEditFamilyDialog(o); if (!o) setEditingFamilyId(null); }}>
        <DialogContent data-testid="edit-family-dialog">
          <DialogHeader>
            <DialogTitle>Edit Family Member</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="col-span-2">
              <Label>Full Name</Label>
              <Input data-testid="edit-family-name" value={familyForm.name} onChange={(e) => setFamilyForm({...familyForm, name: e.target.value})} />
            </div>
            <div>
              <Label>Relationship</Label>
              <select data-testid="edit-family-relationship" value={familyForm.relationship} onChange={(e) => setFamilyForm({...familyForm, relationship: e.target.value})}
                className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-800">
                <option value="spouse">Spouse</option>
                <option value="child">Child</option>
                <option value="parent">Parent</option>
                <option value="sibling">Sibling</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>IC Number</Label>
              <Input data-testid="edit-family-ic" value={familyForm.ic_number} onChange={(e) => setFamilyForm({...familyForm, ic_number: e.target.value})} />
            </div>
            <div>
              <Label>Date of Birth</Label>
              <Input data-testid="edit-family-dob" type="date" value={familyForm.date_of_birth} onChange={(e) => setFamilyForm({...familyForm, date_of_birth: e.target.value})} />
            </div>
            <div>
              <Label>Occupation</Label>
              <Input data-testid="edit-family-occupation" value={familyForm.occupation} onChange={(e) => setFamilyForm({...familyForm, occupation: e.target.value})} />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={familyForm.is_dependent} onChange={(e) => setFamilyForm({...familyForm, is_dependent: e.target.checked})} />
              <Label>Is Dependent (for tax purposes)</Label>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowEditFamilyDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateFamily} data-testid="edit-family-save-btn">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Personal Info Dialog */}
      <Dialog open={showEditPersonalDialog} onOpenChange={setShowEditPersonalDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Personal Information</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="col-span-2">
              <Label>Address Line 1</Label>
              <Input value={personalInfo.address_line1} onChange={(e) => setPersonalInfo({...personalInfo, address_line1: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Address Line 2</Label>
              <Input value={personalInfo.address_line2} onChange={(e) => setPersonalInfo({...personalInfo, address_line2: e.target.value})} />
            </div>
            <div>
              <Label>City</Label>
              <Input value={personalInfo.city} onChange={(e) => setPersonalInfo({...personalInfo, city: e.target.value})} />
            </div>
            <div>
              <Label>State</Label>
              <Input
                data-testid="personal-state-input"
                value={personalInfo.state}
                onChange={(e) => setPersonalInfo({ ...personalInfo, state: e.target.value })}
              />
            </div>
            <div>
              <Label>Postcode / PIN code</Label>
              <Input
                data-testid="personal-postcode-input"
                value={personalInfo.postcode}
                onChange={(e) => handlePostcodeChange(e.target.value)}
                inputMode="numeric"
                pattern="\d{5,6}"
                maxLength={6}
                placeholder="5 digits (MY) or 6 digits (IN)"
              />
              {postcodeLookup.status === 'loading' && (
                <p className="text-xs text-slate-500 mt-1" data-testid="postcode-lookup-status">{postcodeLookup.message}</p>
              )}
              {postcodeLookup.status === 'success' && (
                <p className="text-xs text-emerald-600 mt-1" data-testid="postcode-lookup-status">✓ {postcodeLookup.message}</p>
              )}
              {postcodeLookup.status === 'error' && (
                <p className="text-xs text-red-600 mt-1" data-testid="postcode-lookup-status">{postcodeLookup.message}</p>
              )}
            </div>
            <div>
              <Label>Country</Label>
              <CountrySelect
                data-testid="personal-country-input"
                value={personalInfo.country}
                onChange={(country) => setPersonalInfo({ ...personalInfo, country })}
              />
            </div>
            <div className="col-span-2 border-t pt-4 mt-2">
              <h4 className="font-medium mb-3">Emergency Contact</h4>
            </div>
            <div>
              <Label>Contact Name</Label>
              <Input value={personalInfo.emergency_contact_name} onChange={(e) => setPersonalInfo({...personalInfo, emergency_contact_name: e.target.value})} />
            </div>
            <div>
              <Label>Relationship</Label>
              <Input value={personalInfo.emergency_contact_relation} onChange={(e) => setPersonalInfo({...personalInfo, emergency_contact_relation: e.target.value})} />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input value={personalInfo.emergency_contact_phone} onChange={(e) => setPersonalInfo({...personalInfo, emergency_contact_phone: e.target.value})} />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={personalInfo.emergency_contact_address} onChange={(e) => setPersonalInfo({...personalInfo, emergency_contact_address: e.target.value})} />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowEditPersonalDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePersonalInfo}>Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Leave Entitlement Dialog */}
      <Dialog open={showEditLeaveDialog} onOpenChange={setShowEditLeaveDialog}>
        {/* Iter75 — Make the dialog scrollable on short viewports so the
            Save Changes button is always reachable (was clipped offscreen
            for HR reported by Ankur). max-h with overflow-y on body, and
            sticky bg-aware footer.  */}
        <DialogContent className="max-h-[85vh] overflow-hidden flex flex-col" data-testid="edit-leave-entitlements-dialog">
          <DialogHeader>
            <DialogTitle>Edit Leave Entitlements</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 overflow-y-auto pr-1 flex-1">
            {leaveEntitlements.map((leave, index) => (
              <div key={index} className="grid grid-cols-4 gap-3 items-center p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <Label className="capitalize">{leave.leave_type_name || (leave.leave_type || '').replace(/_/g, ' ')}</Label>
                <div>
                  <Label className="text-xs text-slate-500">Entitled</Label>
                  {/* Iter134 — min="0" so HR can never key in a negative
                      number. Reported by Shikha 19-Jun-2026 (Unpaid
                      Leave saved as -2 and bled into the dashboard). */}
                  <Input type="number" min="0" value={leave.entitled_days} className="h-8"
                    data-testid={`ent-row-${leave.leave_type}-entitled`}
                    onChange={(e) => {
                      const updated = [...leaveEntitlements];
                      updated[index].entitled_days = Math.max(0, parseInt(e.target.value) || 0);
                      setLeaveEntitlements(updated);
                    }} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Carried</Label>
                  <Input type="number" min="0" value={leave.carried_forward || 0} className="h-8"
                    data-testid={`ent-row-${leave.leave_type}-carried`}
                    onChange={(e) => {
                      const updated = [...leaveEntitlements];
                      updated[index].carried_forward = Math.max(0, parseInt(e.target.value) || 0);
                      setLeaveEntitlements(updated);
                    }} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Used</Label>
                  <Input type="number" min="0" value={leave.used_days || 0} className="h-8"
                    data-testid={`ent-row-${leave.leave_type}-used`}
                    onChange={(e) => {
                      const updated = [...leaveEntitlements];
                      updated[index].used_days = Math.max(0, parseInt(e.target.value) || 0);
                      setLeaveEntitlements(updated);
                    }} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 sticky bottom-0">
            <Button variant="outline" onClick={() => setShowEditLeaveDialog(false)} data-testid="edit-leave-cancel-btn">Cancel</Button>
            <Button onClick={handleUpdateLeaveEntitlement} data-testid="edit-leave-save-btn">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* TC-EMP-047: Upload Document Dialog */}
      <Dialog open={showAddDocumentDialog} onOpenChange={setShowAddDocumentDialog}>
        <DialogContent className="sm:max-w-md" data-testid="upload-document-dialog">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Attach a document (PDF, image, or office file up to 10MB) to this employee's profile.
            </DialogDescription>
          </DialogHeader>
          <UploadDocumentForm
            employeeId={employee?.employee_id}
            onUploaded={async () => {
              setShowAddDocumentDialog(false);
              try {
                const docsResponse = await axios.get(`${API}/employees/${employee.employee_id}/documents`);
                setDocuments(docsResponse.data || []);
                toast.success('Document uploaded successfully');
              } catch (e) {
                toast.success('Document uploaded');
              }
            }}
            onCancel={() => setShowAddDocumentDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// TC-EMP-047: Self-contained form so we don't pollute parent state.
function UploadDocumentForm({ employeeId, onUploaded, onCancel }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('general');
  const [name, setName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!file) {
      setError('Please choose a file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large (max 10MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category || 'general');
      if (name) formData.append('name', name);
      await axios.post(`${API}/employees/${employeeId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="doc-file">File</Label>
        <Input
          id="doc-file"
          type="file"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.xls,.xlsx"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="upload-document-file"
        />
      </div>
      <div>
        <Label htmlFor="doc-category">Category</Label>
        <select
          id="doc-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full h-10 px-3 border rounded-md bg-background"
          data-testid="upload-document-category"
        >
          <option value="general">General</option>
          <option value="IC Copy">IC Copy</option>
          <option value="Offer Letter">Offer Letter</option>
          <option value="Resume">Resume</option>
          <option value="Educational Certificate">Educational Certificate</option>
          <option value="Professional Certification">Professional Certification</option>
          <option value="Contract">Contract</option>
        </select>
      </div>
      <div>
        <Label htmlFor="doc-name">Display name (optional)</Label>
        <Input
          id="doc-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. 2024 Educational Certificate"
        />
      </div>
      {error && (
        <p className="text-sm text-red-600" data-testid="upload-document-error">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={uploading}>
          Cancel
        </Button>
        <Button type="submit" disabled={uploading} data-testid="upload-document-submit">
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
    </form>
  );
}

// TC-EMP-037..041: Employee profile photo upload / preview / remove
function EmployeePhotoBlock({ employee, canManage, onChanged }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = React.useRef(null);

  // Authenticated fetch of photo as blob (so we can show <img>)
  useEffect(() => {
    let revokedUrl = null;
    let isMounted = true;
    setError('');
    if (!employee?.profile_photo) {
      setPhotoUrl(null);
      return undefined;
    }
    (async () => {
      try {
        const token = localStorage.getItem('hrms_token');
        const res = await fetch(`${API}/employees/${employee.employee_id}/photo/file`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('photo fetch failed');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revokedUrl = url;
        if (isMounted) setPhotoUrl(url);
      } catch (_) {
        if (isMounted) setPhotoUrl(null);
      }
    })();
    return () => {
      isMounted = false;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [employee?.profile_photo, employee?.employee_id]);

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    // TC-EMP-040: file type
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!allowed.includes(file.type) && !['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      setError('Invalid file type. Only JPG, JPEG, PNG, GIF, WEBP are allowed.');
      toast.error('Invalid file type. Only JPG, JPEG, PNG, GIF, WEBP allowed');
      return;
    }
    // TC-EMP-039: size cap 5MB
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large (max 5MB).');
      toast.error('File too large (max 5MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/employees/${employee.employee_id}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Profile photo updated');
      onChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Upload failed');
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setUploading(true);
    try {
      await axios.delete(`${API}/employees/${employee.employee_id}/photo`);
      toast.success('Photo removed');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2" data-testid="employee-photo-block">
      <div className="h-24 w-24 rounded-full overflow-hidden bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0 ring-2 ring-white dark:ring-slate-700 shadow-md">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={employee.full_name}
            className="h-full w-full object-cover"
            data-testid="employee-photo-img"
          />
        ) : (
          <span className="text-white font-bold text-3xl">
            {employee.full_name.charAt(0)}
          </span>
        )}
      </div>
      {canManage && (
        <div className="flex flex-col gap-1 items-stretch w-full max-w-[160px]">
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="employee-photo-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            data-testid="employee-photo-upload-btn"
          >
            {uploading ? 'Uploading...' : employee.profile_photo ? 'Change Photo' : 'Upload Photo'}
          </Button>
          {employee.profile_photo && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={uploading}
              onClick={handleRemove}
              className="text-red-600 hover:text-red-700"
              data-testid="employee-photo-remove-btn"
            >
              Remove Photo
            </Button>
          )}
          {error && (
            <p className="text-xs text-red-600" data-testid="employee-photo-error">{error}</p>
          )}
        </div>
      )}
    </div>
  );
}

// Helper Components
function InfoRow({ label, value, icon: Icon, bold, note, sub }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex justify-between items-center">
        <span className="text-slate-600 dark:text-slate-400 flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4" />}
          {label}
        </span>
        <span className={`text-slate-900 dark:text-slate-100 ${bold ? 'font-bold' : 'font-medium'}`}>
          {value}
        </span>
      </div>
      {note && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400 text-right" data-testid={`info-note-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}>
          ⚠ {note}
        </p>
      )}
      {sub && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400 text-right" data-testid={`info-sub-${(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
          {sub}
        </p>
      )}
    </div>
  );
}
